import { useState, useEffect } from "react";
import useAuthStore from "../../../store/useAuthStore";
import { PieChart } from "@mui/x-charts/PieChart";
import { Skeleton } from "@mui/material";

const InventoryDashboard = () => {
  const { token } = useAuthStore();

  const [dashboardData, setDashboardData] = useState(null);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lowStockLoading, setLowStockLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
    fetchLowStockAlerts();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/inventory/dashboard", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setDashboardData(data);
      }
    } catch (err) {
      /* silently fail */
    } finally {
      setLoading(false);
    }
  };

  const fetchLowStockAlerts = async () => {
    setLowStockLoading(true);
    try {
      const response = await fetch("/api/inventory/lowStockAlerts", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setLowStockItems(Array.isArray(data.alerts) ? data.alerts : Array.isArray(data) ? data : []);
      }
    } catch (err) {
      /* silently fail */
    } finally {
      setLowStockLoading(false);
    }
  };

  const metrics = [
    {
      label: "Total Items",
      value: dashboardData?.totalItems ?? 0,
      icon: (
        <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
      color: "text-primary",
    },
    {
      label: "Total Inventory Value",
      value: `Nu. ${(dashboardData?.totalInventoryValue ?? 0).toLocaleString()}`,
      icon: (
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: "text-green-600",
    },
    {
      label: "Low Stock Alerts",
      value: dashboardData?.lowStockCount ?? 0,
      icon: (
        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      ),
      color: (dashboardData?.lowStockCount ?? 0) > 0 ? "text-red-500" : "text-gray-700",
    },
    {
      label: "Pending Requisitions",
      value: dashboardData?.pendingRequisitions ?? 0,
      icon: (
        <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
      color: "text-yellow-500",
    },
    {
      label: "Open POs",
      value: dashboardData?.pendingPOs ?? 0,
      icon: (
        <svg className="w-8 h-8 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
        </svg>
      ),
      color: "text-indigo-500",
    },
  ];

  const categoryBreakdown = Array.isArray(dashboardData?.categoryBreakdown) ? dashboardData.categoryBreakdown : [];
  const pieData = categoryBreakdown.map((cat, index) => ({
    id: index,
    value: cat.totalValue || cat.totalItems || 0,
    label: cat.categoryInfo?.[0]?.name || "Uncategorized",
  }));

  const PIE_COLORS = [
    "#408dfb", "#2ecc71", "#e74c3c", "#f39c12", "#9b59b6",
    "#1abc9c", "#e67e22", "#3498db", "#e91e63", "#00bcd4",
  ];

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-6">Inventory Dashboard</h2>

      {/* Metric Cards */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {metrics.map((metric, index) => (
            <div
              key={index}
              className="bg-white shadow rounded-lg p-5 flex flex-col items-center justify-center"
            >
              <div className="mb-3">{metric.icon}</div>
              <p className={`text-2xl font-bold ${metric.color}`}>
                {metric.value}
              </p>
              <p className="text-sm text-gray-500 mt-1 text-center">{metric.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Breakdown Pie Chart */}
        <div className="bg-white shadow rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Category Breakdown</h3>
          {loading ? (
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
          ) : pieData.length === 0 ? (
            <div className="flex items-center justify-center h-[300px]">
              <p className="text-gray-400">No category data available</p>
            </div>
          ) : (
            <div className="flex justify-center">
              <PieChart
                series={[
                  {
                    data: pieData,
                    highlightScope: { faded: "global", highlighted: "item" },
                    faded: { innerRadius: 30, additionalRadius: -30 },
                    innerRadius: 30,
                    outerRadius: 120,
                    paddingAngle: 2,
                    cornerRadius: 4,
                  },
                ]}
                colors={PIE_COLORS}
                height={300}
                width={450}
                slotProps={{
                  legend: {
                    direction: "column",
                    position: { vertical: "middle", horizontal: "right" },
                    padding: { left: 20 },
                    itemMarkWidth: 10,
                    itemMarkHeight: 10,
                    markGap: 5,
                    itemGap: 8,
                    labelStyle: {
                      fontSize: 12,
                    },
                  },
                }}
              />
            </div>
          )}
        </div>

        {/* Low Stock Items */}
        <div className="bg-white shadow rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Low Stock Items</h3>
          {lowStockLoading ? (
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
          ) : lowStockItems.length === 0 ? (
            <div className="flex items-center justify-center h-[300px]">
              <p className="text-gray-400">No low stock items</p>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[340px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">
                      Item Name
                    </th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">
                      Current Stock
                    </th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">
                      Min Threshold
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lowStockItems.map((item, index) => (
                    <tr key={item._id || index} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">
                        <div>
                          <p className="font-medium text-gray-800">{item.name}</p>
                          {item.sku && (
                            <p className="text-xs text-gray-400">{item.sku}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-semibold text-red-600">
                          {item.currentStock}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.minThreshold}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventoryDashboard;
